import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { buttonVariants } from '@/components/ui/button'
import { siteConfig } from '@/constants/site'
import { NAV_LINKS } from '@/constants/landing'

export const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const handleScroll = (): void => setScrolled(window.scrollY > 8)

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const handleKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        const handleResize = (): void => {
            if (window.innerWidth >= 768) setOpen(false)
        }

        window.addEventListener('keydown', handleKey)
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('keydown', handleKey)
            window.removeEventListener('resize', handleResize)
        }
    }, [open])

    const closeMenu = (): void => setOpen(false)

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300',
                scrolled || open
                    ? 'border-border/70 bg-background/85 shadow-[0_1px_0_rgb(20_22_33/0.02)] backdrop-blur-xl'
                    : 'border-transparent bg-transparent'
            )}
        >
            <div className='mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-5 sm:px-6 lg:h-[4.5rem] lg:px-8'>
                <Link
                    href='/'
                    aria-label="Elenix home"
                    className='rounded-xl outline-none focus-visible:ring-3 focus-visible:ring-ring/50'
                    onClick={closeMenu}
                >
                    <Logo />
                </Link>

                <nav aria-label="Main" className='hidden md:block'>
                    <ul className='flex items-center gap-1'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    className='rounded-full px-3.5 py-2 text-sm font-medium text-muted-foreground outline-none transition-colors hover:bg-white hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='hidden items-center gap-2 md:flex'>
                    <a
                        href={siteConfig.appUrl}
                        className={cn(buttonVariants({ variant: 'ghost', size: 'pill' }), 'text-foreground hover:bg-white')}
                    >
                        Sign in
                    </a>

                    <a
                        href='/#demo'
                        className={cn(buttonVariants({ size: 'pill' }), 'shadow-[0_8px_20px_-10px_rgb(53_102_219/0.9)]')}
                    >
                        Book a demo
                    </a>
                </div>

                <button
                    type='button'
                    aria-expanded={open}
                    aria-controls='mobile-menu'
                    aria-label={open ? "Close menu" : "Open menu"}
                    onClick={() => setOpen(current => !current)}
                    className='grid size-10 place-items-center rounded-full border border-border bg-white text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 md:hidden'
                >
                    {open ? <X className='size-5' /> : <Menu className='size-5' />}
                </button>
            </div>

            <div
                id='mobile-menu'
                hidden={!open}
                className='border-t border-border/70 md:hidden'
            >
                <nav aria-label="Mobile" className='mx-auto max-w-6xl px-5 pb-6 pt-3 sm:px-6'>
                    <ul className='flex flex-col'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    onClick={closeMenu}
                                    className='flex items-center justify-between rounded-xl px-3 py-3.5 text-base font-medium text-foreground outline-none transition-colors hover:bg-white focus-visible:ring-3 focus-visible:ring-ring/50'
                                >
                                    {link.label}
                                    <ArrowRight className='size-4 text-muted-foreground' />
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-4 grid grid-cols-2 gap-3'>
                        <a
                            href={siteConfig.appUrl}
                            className={cn(buttonVariants({ variant: 'outline', size: 'pill-lg' }), 'bg-white')}
                        >
                            Sign in
                        </a>

                        <a
                            href='/#demo'
                            onClick={closeMenu}
                            className={buttonVariants({ size: 'pill-lg' })}
                        >
                            Book a demo
                        </a>
                    </div>
                </nav>
            </div>
        </header>
    )
}
